import React, { useState } from "react";
import { Button, Stack } from "react-bootstrap";
import { Link } from "react-router-dom";

import GameActionModal from "./GameActionModal";

import { alertActions } from "../features/alert/alertSlice";
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import { Breakpoint } from "../hooks/useBreakpoint";
import {
    acceptReschedule,
    cancelReschedule,
    declineReschedule,
    deleteGame,
    postponeGame,
    resetGame,
} from "../lib/api";
import { isXOrSmaller } from "../lib/utils";
import { IGame, ILeague, ISiteSetting, ITeam } from "../types/api";
import { IAlertParams } from "../types/shared";

const GameActionButtons = ({
    game,
    selectedTeam,
    selectedLeague,
    displayDeleteLink,
    handleDelete,
    setSchedule,
    breakpoint,
    scheduleType,
}: {
    game: IGame;
    selectedTeam?: ITeam;
    selectedLeague?: ILeague;
    displayDeleteLink?: ISiteSetting["setting"];
    handleDelete: (gameId: number) => void;
    setSchedule: React.Dispatch<React.SetStateAction<IGame[]>>;
    breakpoint: Breakpoint;
    scheduleType: "team" | "league";
}) => {
    const { id, home: homeTeam } = game;
    const [show, setShow] = useState<Record<string, boolean>>({
        delete: false,
        postpone: false,
        cancel: false,
        accept: false,
        decline: false,
        reset: false,
    });
    const user = useAppSelector(state => state.auth.user);
    const dispatch = useAppDispatch();
    const today = new Date();
    const gameDate = new Date(game.date);

    const handleClose = (key: string) =>
        setShow(prev => ({
            ...prev,
            [key]: false,
        }));

    const handleShow = (key: string) =>
        setShow(prev => ({
            ...prev,
            [key]: true,
        }));

    const handleAction = async (
        action: keyof typeof show,
        gameId: IGame["id"],
    ) => {
        switch (action) {
            case "delete":
                await deleteGame(gameId, user!.id)
                    .then(response => {
                        dispatch(
                            alertActions.success({
                                title: `Game deleted successfully`,
                                dismissible: true,
                            }),
                        );
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem deleting the game.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                handleDelete(gameId);
                break;
            case "postpone":
                await postponeGame(
                    gameId,
                    game.league,
                    user!.id,
                    scheduleType,
                    selectedTeam?.id,
                )
                    .then(response => {
                        dispatch(
                            alertActions.success({
                                title: `Game postponed successfully`,
                                dismissible: true,
                            }),
                        );

                        setSchedule(response.schedule.games || []);
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem postponing the game.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
            case "accept":
                await acceptReschedule(
                    gameId,
                    game.league,
                    user!.id,
                    scheduleType,
                    selectedTeam?.id,
                )
                    .then(response => {
                        dispatch(
                            alertActions.success({
                                title: `Game reschedule accepted`,
                                dismissible: true,
                            }),
                        );

                        setSchedule(response.schedule.games);
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem accepting the reschedule request.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
            case "cancel":
                await cancelReschedule(
                    gameId,
                    game.league,
                    user!.id,
                    scheduleType,
                    selectedTeam?.id,
                )
                    .then(response => {
                        dispatch(
                            alertActions.success({
                                title: `Game reschedule cancelled`,
                                dismissible: true,
                            }),
                        );

                        setSchedule(response.schedule.games);
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem cancelling the reschedule request.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
            case "decline":
                await declineReschedule(
                    gameId,
                    game.league,
                    user!.id,
                    scheduleType,
                    selectedTeam?.id,
                )
                    .then(response => {
                        dispatch(
                            alertActions.success({
                                title: `Game reschedule cancelled`,
                                dismissible: true,
                            }),
                        );

                        setSchedule(response.schedule.games);
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem cancelling the reschedule request.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
            case "reset":
                await resetGame(gameId)
                    .then(response => {
                        let alertMessage: string;
                        let alertType: IAlertParams["level"];
                        switch (response.message) {
                            case "Nothing to reset":
                                alertMessage =
                                    "Can not reset a game without submitted scores";
                                alertType = "warning";
                                break;
                            default:
                                alertMessage = "Game reset successfully";
                                alertType = "success";
                        }

                        dispatch(
                            alertActions[alertType]({
                                title: alertMessage,
                                dismissible: true,
                            }),
                        );
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem resetting the game.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
        }

        handleClose(action);
    };

    return (
        <>
            <tr className="gameActions" data-testid="gameActionButtons">
                <td colSpan={isXOrSmaller("md", breakpoint) ? 7 : 8}>
                    <Stack direction="horizontal" gap={2}>
                        {/* Delete Game */}
                        {(displayDeleteLink === "true" ||
                            user!.userLevel <= 2) && (
                            <Button onClick={() => handleShow("delete")}>
                                Delete
                            </Button>
                        )}

                        {/* Postpone Game */}
                        {game.status !== "Postponed" &&
                            ((selectedTeam &&
                                homeTeam.id ===
                                    parseInt(selectedTeam.id.toString()) &&
                                new Date(gameDate.getTime() + 120 * 60000) >
                                    today &&
                                game.isPlayoff === 0) ||
                                user!.userLevel <= 2) && (
                                <Button onClick={() => handleShow("postpone")}>
                                    Postpone
                                </Button>
                            )}

                        {/* Reschedule Game */}
                        {game.status === "Scheduled" &&
                            ((game.isPlayoff === 1
                                ? selectedTeam &&
                                  homeTeam.id ===
                                      parseInt(selectedTeam.id.toString())
                                : true) ||
                                user!.userLevel <= 2) && (
                                <Link
                                    className="btn btn-primary"
                                    to={`${
                                        selectedTeam
                                            ? `/coachs-clubhouse/team/${selectedTeam.id}/schedule/reschedule-game/${id}`
                                            : `/league-management/league/${selectedLeague?.id}/schedule/reschedule-game/${id}`
                                    }`}
                                >
                                    Reschedule
                                </Link>
                            )}

                        {/* Reschedule a Postponed Game */}
                        {game.status === "Postponed" &&
                            game.rescheduleInfo === null && (
                                <Link
                                    className="btn btn-primary"
                                    to={`${
                                        selectedTeam
                                            ? `/coachs-clubhouse/team/${selectedTeam.id}/schedule/reschedule-game/${id}`
                                            : `/league-management/league/${selectedLeague?.id}/schedule/reschedule-game/${id}`
                                    }`}
                                >
                                    Propose new date/time
                                </Link>
                            )}

                        {/* Cancel Reschedule Request */}
                        {game.status !== "Scheduled" &&
                            game.rescheduleInfo &&
                            game.rescheduleInfo.submittedBy === user!.id && (
                                <Button onClick={() => handleShow("cancel")}>
                                    Cancel Reschedule
                                </Button>
                            )}

                        {/* Accept/Decline Reschedule Request */}
                        {game.status !== "Scheduled" &&
                            game.rescheduleInfo &&
                            game.rescheduleInfo.lastUpdatedBy !== user!.id && (
                                <>
                                    <Button
                                        onClick={() => handleShow("accept")}
                                    >
                                        Accept Reschedule
                                    </Button>
                                    <Button
                                        onClick={() => handleShow("decline")}
                                    >
                                        Decline Reschedule
                                    </Button>
                                </>
                            )}

                        {/* View/Enter Game Details */}
                        <Link
                            className="btn btn-primary"
                            to={`${
                                selectedTeam
                                    ? `/coachs-clubhouse/team/${
                                          selectedTeam!.id
                                      }/schedule/game/${id}/summary`
                                    : `/league-management/league/${selectedLeague!.id}/schedule/game/${id}/summary`
                            }`}
                        >
                            Details/Summary
                        </Link>

                        {/* View/Enter Game Logs */}
                        {user!.userLevel <= 2 && (
                            <Link
                                className="btn btn-primary"
                                to={`${
                                    selectedTeam
                                        ? `/coachs-clubhouse/team/${selectedTeam!.id}/schedule/game/${id}/logs`
                                        : `/league-management/game-logs/${game.id}`
                                }`}
                            >
                                View Game Logs
                            </Link>
                        )}

                        {/* Reset Game */}
                        {user!.userLevel <= 2 && (
                            <Button
                                onClick={() => handleShow("reset")}
                                variant="danger"
                            >
                                Reset Game
                            </Button>
                        )}
                    </Stack>
                </td>
            </tr>
            {Object.entries(show).map(([key, value]) => (
                <GameActionModal
                    key={key}
                    action={key}
                    show={value}
                    handleClose={handleClose}
                    handleAction={handleAction}
                    game={game}
                />
            ))}
        </>
    );
};

export default GameActionButtons;
